// API functions for Holiday Requests
import { getSessionUUID } from '../components/lib/AuthToken'; // Import the utility function
import logger from "../utils/logger";

/**
 * Fetch only the count of pending holiday requests for performance
 */
export const fetch_pending_holiday_requests_count = async () => {
    try {
        const sessionToken = await getSessionUUID();

        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        const response = await fetch(`/holiday_requests/pending/count`, requestOptions);
        const responseBody = await response.json();

        return {
            message: 'Count request successful',
            status: response.status,
            count: responseBody.count || 0,
            success: responseBody.success || false
        };
    } catch (err) {
        logger.error("Error fetching holiday requests count:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            count: 0,
            success: false
        };
    }
};

/**
 * Fetch all pending and cancelled holiday requests for admin review
 */
export const fetch_pending_holiday_requests = async () => {
    try {
        logger.log("*** Starting holiday requests fetch ***");
        const sessionToken = await getSessionUUID(); // Use the utility function
        logger.log("*** Got session token:", sessionToken ? 'YES' : 'NO');

        // Options to send data to backend
        const requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            }
        };

        // Send Request to backend
        const response = await fetch(`/holiday_requests/pending`, requestOptions);
        const responseBody = await response.json();

        // Return the message and status
        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody.data || [],
            success: responseBody.success || false
        };
    } catch (err) {
        logger.error("Error fetching holiday requests:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            data: [],
            success: false
        };
    }
};

/**
 * Update holiday request status (approve/reject)
 */
export const update_holiday_request = async (requestId, status, comments, approvedBy) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function

        // User input
        const body = {
            status,
            approval_comments: comments,
            approved_by: approvedBy
        };

        // Options to send data to backend
        const requestOptions = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(body)
        };

        // Send Request to backend
        const response = await fetch(`/holiday_requests/${requestId}`, requestOptions);
        const responseBody = await response.json();

        // Return the message and status
        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody.data || {},
            success: responseBody.success || false
        };
    } catch (err) {
        logger.error("Error updating holiday request:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            data: {},
            success: false
        };
    }
};

/**
 * Create holiday entry in holidays table (when approved)
 */
export const create_holiday_entry = async (holidayData) => {
    try {
        const sessionToken = await getSessionUUID(); // Use the utility function

        // User input
        const body = {
            holiday_data: holidayData
        };

        // Options to send data to backend
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(body)
        };

        // Send Request to backend
        const response = await fetch(`/holidays`, requestOptions);
        const responseBody = await response.json();

        // Return the message and status
        return {
            message: 'Request successful',
            status: response.status,
            data: responseBody.data || {},
            success: responseBody.success || false
        };
    } catch (err) {
        logger.error("Error creating holiday entry:", err);
        return {
            message: err.message || 'An error occurred',
            status: 500,
            data: {},
            success: false
        };
    }
};

/**
 * Create a new pending holiday request from the web app
 */
export const create_holiday_request = async (requestData) => {
    try {
        const sessionToken = await getSessionUUID();
        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`
            },
            body: JSON.stringify(requestData)
        };
        const response = await fetch(`/holiday_requests/create`, requestOptions);
        const responseBody = await response.json();
        return {
            success: response.ok && responseBody.success,
            status: response.status,
            data: responseBody.data || {},
            error: responseBody.error || 'Failed to submit holiday request'
        };
    } catch (err) {
        logger.error("Error creating holiday request:", err);
        return {
            success: false,
            status: 500,
            error: err.message || 'An error occurred'
        };
    }
};